import { z } from "zod";

/** Admin "acting as" target. Workspace ids are opaque text ids, bounded here. */
export const actAsSchema = z
  .object({
    workspaceId: z.string().trim().min(1).max(64),
  })
  .strict();

export type ActAsInput = z.infer<typeof actAsSchema>;

const adminId = z.string().trim().min(1).max(64);
const isoCursor = z.string().trim().min(1).max(200);

export const WORKSPACE_STATUSES = ["pending", "active", "suspended", "deleted"] as const;

export const adminWorkspaceQuerySchema = z.object({
  status: z.enum(WORKSPACE_STATUSES).optional(),
  q: z.string().trim().max(120).optional(),
});

export const adminWorkspacePatchSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    plan: z.string().trim().min(1).max(32).optional(),
    quota_bytes: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).optional(),
    quota_persons: z.number().int().min(0).max(100_000).optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, "Nothing to update");

export const transferOwnershipSchema = z.object({ operatorId: adminId }).strict();

export const adminOperatorQuerySchema = z.object({ q: z.string().trim().max(120).optional() });

export const adminOperatorPatchSchema = z
  .object({ email: z.string().trim().toLowerCase().email().max(254) })
  .strict();

export const auditQuerySchema = z.object({
  workspaceId: adminId.optional(),
  actorKind: z.enum(["operator", "admin", "player"]).optional(),
  action: z.string().trim().min(1).max(64).optional(),
  before: isoCursor.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const errorQuerySchema = z.object({
  workspaceId: adminId.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const takedownSchema = z.object({ reason: z.string().trim().min(1).max(500) }).strict();

/*
 * Response contracts for the admin endpoints. The API validates its output with these
 * (so a drifting repo shape fails loudly) and the admin UI derives its types from them.
 * Field names are camelCase, exactly as the API serialises them.
 */

const isoOrNull = z.string().nullable();
const count = z.number().int().nonnegative();

export const adminWorkspaceRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  ownerId: z.string().nullable(),
  ownerEmail: z.string().nullable(),
  plan: z.string(),
  quotaBytes: count,
  quotaPersons: count,
  suspendedAt: isoOrNull,
  deletedAt: isoOrNull,
  createdAt: z.string(),
  status: z.enum(WORKSPACE_STATUSES),
  pendingInviteEmail: z.string().nullable(),
  members: count,
  persons: count,
  configurations: count,
  bytesUsed: count,
});
export type AdminWorkspaceRow = z.infer<typeof adminWorkspaceRowSchema>;

export const adminOperatorRowSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  isAdmin: z.boolean(),
  identities: z.array(z.string()),
  workspaces: z.array(
    z.object({
      workspaceId: z.string(),
      name: z.string(),
      role: z.string(),
    }),
  ),
  lastLoginAt: isoOrNull,
});
export type AdminOperatorRow = z.infer<typeof adminOperatorRowSchema>;

export const auditRowSchema = z.object({
  id: z.string(),
  workspaceId: z.string().nullable(),
  actorKind: z.enum(["operator", "admin", "player"]),
  actorId: z.string(),
  action: z.string(),
  target: z.string().nullable(),
  metaJson: z.string().nullable(),
  at: z.string(),
});
export type AuditRowResponse = z.infer<typeof auditRowSchema>;

export const auditPageSchema = z.object({
  rows: z.array(auditRowSchema),
  nextCursor: z.string().nullable(),
});
export type AuditPageResponse = z.infer<typeof auditPageSchema>;

export const errorRowSchema = z.object({
  id: z.string(),
  workspaceId: z.string().nullable(),
  level: z.enum(["warn", "error"]),
  message: z.string(),
  /** JSON text. Written by the 500 handler as { name, route }. */
  metaJson: z.string().nullable(),
  at: z.string(),
});
export type ErrorRowResponse = z.infer<typeof errorRowSchema>;

export const VALIDATION_CODES = [
  "export_invalid",
  "media_missing",
  "media_object_missing",
  "region_invalid",
  "hotkey_duplicate",
] as const;

export const configurationValidationSchema = z.object({
  ok: z.boolean(),
  issues: z.array(
    z.object({
      code: z.enum(VALIDATION_CODES),
      cellPosition: z.number().int().optional(),
      detail: z.string(),
    }),
  ),
});
export type ConfigurationValidationResponse = z.infer<typeof configurationValidationSchema>;

/** Operator-side member row of a workspace (the admin members list). */
export const adminMemberRowSchema = z.object({
  operatorId: z.string(),
  role: z.enum(["owner", "member"]),
  email: z.string(),
  name: z.string(),
});
export type AdminMemberRow = z.infer<typeof adminMemberRowSchema>;
